import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Boxes, Package, TrendingUp, AlertCircle, DollarSign, Tags, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const { t } = useLanguage();
    const { user } = useAuth();

    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API_URL}/inventory/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold">{t('dashboard')}</h1>
                    <p className="text-muted-foreground">{t('dashboardSubtitle')}</p>
                </div>

                {/* Main Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <Boxes className="h-4 w-4" />
                                {t('availableUnits')}
                            </CardDescription>
                            <CardTitle className="text-3xl">{stats?.counts?.available || 0}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center text-sm text-green-500">
                                <TrendingUp className="h-4 w-4 mr-1" />
                                {t('readyForSale')}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <Package className="h-4 w-4" />
                                {t('pendingOrders')}
                            </CardDescription>
                            <CardTitle className="text-3xl">{stats?.counts?.ordered || 0}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center text-sm text-amber-500">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {t('awaitingDispatch')}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                {t('returnsQueue')}
                            </CardDescription>
                            <CardTitle className="text-3xl">{stats?.counts?.returned || 0}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center text-sm text-purple-500">
                                <Boxes className="h-4 w-4 mr-1" />
                                {t('pendingInspection')}
                            </div>
                        </CardContent>
                    </Card>

                    {user?.role !== 'TYPE_A' && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardDescription className="flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    {t('warehouseValue')}
                                </CardDescription>
                                <CardTitle className="text-3xl">
                                    ${parseFloat(stats?.warehouseValue || 0).toFixed(2)}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center text-sm text-muted-foreground">
                                    <TrendingUp className="h-4 w-4 mr-1" />
                                    {t('totalCostBasis')}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Secondary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4" />
                                {t('soldUnits')}
                            </CardDescription>
                            <CardTitle className="text-2xl text-green-600">{stats?.counts?.sold || 0}</CardTitle>
                        </CardHeader>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <Package className="h-4 w-4" />
                                {t('totalProducts')}
                            </CardDescription>
                            <CardTitle className="text-2xl">{stats?.productCount || 0}</CardTitle>
                        </CardHeader>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <Tags className="h-4 w-4" />
                                {t('categories')}
                            </CardDescription>
                            <CardTitle className="text-2xl">{stats?.categoryCount || 0}</CardTitle>
                        </CardHeader>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                {t('damagedUnits')}
                            </CardDescription>
                            <CardTitle className="text-2xl text-destructive">{stats?.counts?.damaged || 0}</CardTitle>
                        </CardHeader>
                    </Card>
                </div>

                {/* Status Breakdown */}
                <Card>
                    <CardHeader>
                        <CardTitle>{t('inventoryStatusBreakdown')}</CardTitle>
                        <CardDescription>{t('inventoryDistribution').replace('{0}', stats?.counts?.total || 0)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                            <div className="text-center p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                                <div className="text-2xl font-bold text-green-500">{stats?.counts?.available || 0}</div>
                                <div className="text-sm text-muted-foreground">{t('available')}</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                <div className="text-2xl font-bold text-amber-500">{stats?.counts?.ordered || 0}</div>
                                <div className="text-sm text-muted-foreground">{t('ordered')}</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                <div className="text-2xl font-bold text-blue-500">{stats?.counts?.pendingDelivery || 0}</div>
                                <div className="text-sm text-muted-foreground">{t('inTransit')}</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                <div className="text-2xl font-bold text-emerald-500">{stats?.counts?.sold || 0}</div>
                                <div className="text-sm text-muted-foreground">{t('sold')}</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                                <div className="text-2xl font-bold text-purple-500">{stats?.counts?.returned || 0}</div>
                                <div className="text-sm text-muted-foreground">{t('returned')}</div>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                                <div className="text-2xl font-bold text-red-500">{stats?.counts?.damaged || 0}</div>
                                <div className="text-sm text-muted-foreground">{t('damaged')}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
